import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

type AmountLine = {
    name: string;
    amount: number;
};

type ProfitReportData = {
    startDate: string;
    endDate: string;
    activities: AmountLine[];
    salaries: AmountLine[];
    operatingExpenses: AmountLine[];
};

type PdfWithLastTable = jsPDF & {
    lastAutoTable?: { finalY: number };
};

const money = (value: number) => `$${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(value)}`;

const totalOf = (lines: AmountLine[]) => lines.reduce((total, line) => total + line.amount, 0);

const tableEnd = (doc: jsPDF, fallback: number) =>
    (doc as PdfWithLastTable).lastAutoTable?.finalY ?? fallback;

const addSectionTitle = (doc: jsPDF, title: string, y: number) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 20, y);
};

const ensureSpace = (doc: jsPDF, y: number, requiredHeight: number) => {
    if (y + requiredHeight <= 275) return y;
    doc.addPage();
    return 20;
};

const addAmountTable = (
    doc: jsPDF,
    startY: number,
    headers: [string, string],
    lines: AmountLine[],
    totalLabel: string,
) => {
    const body = lines.length
        ? lines.map((line) => [line.name, money(line.amount)])
        : [["None", money(0)]];

    autoTable(doc, {
        startY,
        margin: { left: 30, right: 30 },
        head: [headers],
        body,
        foot: [[totalLabel, money(totalOf(lines))]],
        theme: "grid",
        styles: {
            font: "helvetica",
            fontSize: 10,
            cellPadding: 3,
            lineColor: [160, 160, 160],
            lineWidth: 0.2,
        },
        headStyles: {
            fillColor: [32, 32, 32],
            textColor: [255, 255, 255],
            fontStyle: "bold",
        },
        footStyles: {
            fillColor: [245, 245, 245],
            textColor: [20, 20, 20],
            fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: [247, 247, 247] },
        columnStyles: {
            0: { cellWidth: 105 },
            1: { halign: "right" },
        },
        didParseCell: (data) => {
            if (data.column.index === 1) data.cell.styles.halign = "right";
        },
    });

    return tableEnd(doc, startY) + 12;
};

export const generateMonthlyProfitReport = ({
    startDate,
    endDate,
    activities,
    salaries,
    operatingExpenses,
}: ProfitReportData) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const totalRevenue = totalOf(activities);
    const totalSalaryFees = totalOf(salaries);
    const totalOperatingExpenses = totalOf(operatingExpenses);
    const totalDeductions = totalSalaryFees + totalOperatingExpenses;
    const netProfit = totalRevenue - totalDeductions;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(15, 15, 15);
    doc.text("Monthly Revenue & Net Profit Report", 105, 20, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(`Period: ${startDate} to ${endDate}`, 105, 29, { align: "center" });

    let y = 43;
    addSectionTitle(doc, "1. Total Revenue by Activity", y);
    y = addAmountTable(doc, y + 6, ["Activity", "Revenue (USD)"], activities, "Total Income");

    y = ensureSpace(doc, y, 45);
    addSectionTitle(doc, "2. Salary Fees", y);
    y = addAmountTable(doc, y + 6, ["Coach", "Salary Fee (USD)"], salaries, "Total Salary Fees");

    y = ensureSpace(doc, y, 45);
    addSectionTitle(doc, "3. Operating Expenses", y);
    y = addAmountTable(
        doc,
        y + 6,
        ["Expense", "Amount (USD)"],
        operatingExpenses,
        "Total Operating Expenses",
    );

    y = ensureSpace(doc, y, 68);
    addSectionTitle(doc, "4. Profit Summary", y);
    autoTable(doc, {
        startY: y + 6,
        margin: { left: 30, right: 30 },
        body: [
            ["Total Income", money(totalRevenue)],
            ["Salary Fees", `- ${money(totalSalaryFees)}`],
            ["Operating Expenses", `- ${money(totalOperatingExpenses)}`],
            ["Total Deductions", `- ${money(totalDeductions)}`],
            ["Net Profit", money(netProfit)],
        ],
        theme: "grid",
        styles: {
            font: "helvetica",
            fontSize: 11,
            cellPadding: 3.5,
            lineColor: [140, 140, 140],
            lineWidth: 0.2,
        },
        columnStyles: {
            0: { fontStyle: "bold", cellWidth: 105 },
            1: { halign: "right", fontStyle: "bold" },
        },
        didParseCell: (data) => {
            if (data.row.index === 4) {
                data.cell.styles.fillColor = netProfit >= 0 ? [226, 246, 232] : [254, 226, 226];
                data.cell.styles.textColor = netProfit >= 0 ? [20, 110, 55] : [180, 25, 25];
            }
        },
    });

    const summaryEnd = tableEnd(doc, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(netProfit >= 0 ? 20 : 180, netProfit >= 0 ? 110 : 25, netProfit >= 0 ? 55 : 25);
    doc.text(`Net Profit: ${money(netProfit)}`, 105, summaryEnd + 13, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text(
        `${money(totalRevenue)} income - ${money(totalSalaryFees)} salaries - ${money(totalOperatingExpenses)} operating expenses = ${money(netProfit)}`,
        105,
        summaryEnd + 20,
        { align: "center", maxWidth: 170 },
    );

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(130, 130, 130);
        doc.text(`Page ${page} of ${pageCount}`, 190, 289, { align: "right" });
    }

    doc.save(`monthly-profit-report-${startDate}-${endDate}.pdf`);
};
